import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from '@/lib/auth-context';
import { ProfileProvider, useProfile } from '@/lib/profile-context';
import { Toaster } from '@/components/ui/sonner';
import LoginPage from '@/pages/LoginPage';
import SignupPage from '@/pages/SignupPage';
import DashboardLayout from '@/components/layout/DashboardLayout';
import OverviewPage from '@/pages/OverviewPage';
import MetricsPage from '@/pages/MetricsPage';
import AlertsPage from '@/pages/AlertsPage';
import SignalsPage from '@/pages/SignalsPage';
import HistoryPage from '@/pages/HistoryPage';
import AdminPage from '@/pages/AdminPage';
import SettingsPage from '@/pages/SettingsPage';
import AsinRiskPage from '@/pages/AsinRiskPage';
import CalendlyPage from '@/pages/CalendlyPage';
import PoaWizardPage from '@/pages/PoaWizardPage';
import AccountsPage from '@/pages/AccountsPage';
import UsersPage from '@/pages/UsersPage';
import BookNoraPage from '@/pages/BookNoraPage';
import ForgotPasswordPage from '@/pages/ForgotPasswordPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import SupportDataPage from '@/pages/SupportDataPage';
import AuditReportPage from '@/pages/AuditReportPage';
import GuestOnlyRoute from '@/components/auth/GuestOnlyRoute';
import AuthRedirectListener from '@/components/auth/AuthRedirectListener';
import RequireActivePlan from '@/components/auth/RequireActivePlan';
import RequirePlanFeature from '@/components/auth/RequirePlanFeature';
import PoaOnlyGuard from '@/components/auth/PoaOnlyGuard';
import PoaTemplatesGate from '@/components/auth/PoaTemplatesGate';
import '@/App.css';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen bg-background flex items-center justify-center"><div className="animate-pulse text-muted-foreground">Loading...</div></div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function RequireProfile({ children }) {
  const { hasSelection } = useProfile();
  if (!hasSelection) return <Navigate to="/accounts" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, loading, isAdmin } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/" replace />;
  if (!isAdmin) return <Navigate to="/" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<GuestOnlyRoute><LoginPage /></GuestOnlyRoute>} />
      <Route path="/signup" element={<GuestOnlyRoute><SignupPage /></GuestOnlyRoute>} />
      <Route path="/forgot-password" element={<GuestOnlyRoute><ForgotPasswordPage /></GuestOnlyRoute>} />
      <Route path="/reset-password" element={<GuestOnlyRoute><ResetPasswordPage /></GuestOnlyRoute>} />
      <Route path="/" element={<ProtectedRoute><RequireActivePlan><PoaOnlyGuard><DashboardLayout /></PoaOnlyGuard></RequireActivePlan></ProtectedRoute>}>
        <Route index element={<RequirePlanFeature feature="monitor"><RequireProfile><OverviewPage /></RequireProfile></RequirePlanFeature>} />
        <Route path="metrics" element={<RequirePlanFeature feature="monitor"><RequireProfile><MetricsPage /></RequireProfile></RequirePlanFeature>} />
        <Route path="alerts" element={<RequirePlanFeature feature="monitor"><RequireProfile><AlertsPage /></RequireProfile></RequirePlanFeature>} />
        <Route path="signals" element={<RequirePlanFeature feature="monitor"><RequireProfile><SignalsPage /></RequireProfile></RequirePlanFeature>} />
        <Route path="history" element={<RequirePlanFeature feature="monitor"><RequireProfile><HistoryPage /></RequireProfile></RequirePlanFeature>} />
        <Route path="asin-risks" element={<RequirePlanFeature feature="monitor"><RequireProfile><AsinRiskPage /></RequireProfile></RequirePlanFeature>} />
        <Route path="book-call" element={<CalendlyPage />} />
        <Route path="poa-wizard" element={<RequirePlanFeature feature="poa"><PoaWizardPage /></RequirePlanFeature>} />
        <Route path="poa-templates" element={<RequirePlanFeature feature="poa"><PoaTemplatesGate /></RequirePlanFeature>} />
        <Route path="support-data" element={<RequirePlanFeature feature="monitor"><RequireProfile><SupportDataPage /></RequireProfile></RequirePlanFeature>} />
        <Route path="accounts" element={<AccountsPage />} />
        <Route path="audit-report" element={<RequirePlanFeature feature="audit_report"><RequireProfile><AuditReportPage /></RequireProfile></RequirePlanFeature>} />
        <Route path="admin" element={<AdminRoute><AdminPage /></AdminRoute>} />
        <Route path="users" element={<AdminRoute><UsersPage /></AdminRoute>} />
        <Route path="book-nora" element={<BookNoraPage />} />
        <Route path="meetings" element={<Navigate to="/book-nora" replace />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ProfileProvider>
          <div className="dark min-h-screen bg-background text-foreground">
            <AuthRedirectListener />
            <AppRoutes />
            <Toaster position="top-right" richColors />
          </div>
        </ProfileProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
